import { useMemo } from 'react';

export const cacheTags = {
  PERSONS: 'Person',
  CLASSES: 'Class',
  DIVISIONS: 'Division',
  CLASS_DIVISIONS: 'Division',
  SUBJECTS: 'Subject',
  SUBJECT_CONTAINER: 'SubjectContainer',
  SCHOOL_YEAR: 'planung',
  ROOMS: 'Room',
  LESSONS: 'Lesson',
  LESSONS_WITHOUT_CARDS: 'LESSONS_WITHOUT_CARDS',
  TIME_GRID: 'planung',
  BUILDINGS: 'planung',
  TIMETABLE: 'Timetable',
  TIMETABLE_BLOCK: 'TimetableBlock',
  DEPENDENCIES: 'DependencyItem',
  TEACHINGLOADFACTOR: 'TeachingLoadFactorItem',
  VERSIONS: 'VERSIONS',
  VERSIONS_BOARD: 'VERSIONS_BOARD',
  TIMETABLE_VERSIONS: 'TimetableVersion',
  CLASSES_MATRIX: 'classes_matrix',
  ROOMS_MATRIX: 'rooms_matrix',
  SUBJECT_MATRIX: 'subject_matrix',
  TEACHER_MATRIX: 'teacher_matrix',
  CLASSES_CONSTRAINTS: 'CLASSES_CONSTRAINTS',
  GRADE_GROUPS: 'GradeGroup',
  SUBJECT_GROUPS: 'subject_group',
  CLASS_GRID_CARDS: 'ClassGridCard',
  ORGANIZATION: 'organization',
  APPLICATION_GRANT: 'application_grant',
  PIM_PROFILE: 'pim_profile',
  CURRICULA: 'CURRICULA',
  CURRICULUM_CLASSES: 'CURRICULUM_CLASSES',
  CURRICULUM_VERSIONS: 'CURRICULUM_VERSIONS',
  LABELS: 'LABELS',
  ADDITIONAL_TEACHINGLOAD: 'AdditionalTeachingLoadItem',
  DEPUTATE: 'DEPUTATE',
  TEACHINGBLOCK: 'TeachingBlock',
  TEACHINGBLOCK_VERSIONS: 'TeachingBlockVersion',
  TEACHINGBLOCK_LESSONS: 'TEACHINGBLOCK_LESSONS',
  SUBJECT_GRID_CARDS: 'SUBJECT_GRID_CARDS',
  TEACHING_BLOCK_CARDS: 'TeachingBlockCard',
  TIMETABLE_CARD: 'TimetableCard',
  LESSON_UNITS: 'LessonUnits',
  TEACHING_BLOCK_AVAILABILITY_EXCLUSION: 'TeachingBlockAvailabilityExclusion',
  TIMETABLE_PRINT_CONFIG: 'TimetablePrintConfigItem',
  TEACHING_LOAD_REPORT_CONFIG: 'TeachingLoadReportConfigItem',
  LESSON_CLASS: 'LessonClass',
  EVENT: 'EventItem',
  SCHOOL_EVENT: 'SchoolEvent',
  GROUPS: 'Group',
  ABSENCES: 'AbsenceEntry',
  SUBSTITUTION_ANNOUNCEMENTS: 'SubstitutionAnnouncementItem',
  SUBSTITUTION_APP_CONFIG: 'SubstitutionAppConfigItem',
  SUBSTITUTION_PLAN_CONFIG: 'SubstitutionPrintConfigItem',
};

function useMemorizedCacheTag(tag: keyof typeof cacheTags, suffix?: string | null) {
  return useMemo(() => {
    return getCacheTag(tag, suffix);
  }, [tag, suffix]);
}

function useMemorizedCacheTags(tags: Array<keyof typeof cacheTags>) {
  // Auf den Tag-Inhalt memoisieren statt auf die Array-Referenz: ein Inline-Array (['A','B']) ist
  // bei jedem Render eine neue Referenz, würde das Memo also nutzlos machen und – an eine urql-Query
  // übergeben – einen Render-Loop auslösen. Tag-Keys enthalten keine Kommas, daher ist der Join eindeutig.
  const key = tags.join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => getCacheTags(tags), [key]);
}

/**
 * Rückgabetypen der handgeschriebenen Root-Felder (Suffix "Item"), die zu einem Tag gehören.
 *
 * Ein Tag wie SUBJECTS steht für den Typnamen `Subject`, der weiter in generierten Beziehungen
 * vorkommt (z. B. `lessons { subject }`). Die eigenen Root-Felder liefern aber `SubjectItem`.
 * Damit eine Mutation mit diesem Tag auch Queries verwirft, die Fächer nur als `SubjectItem`
 * enthalten und selbst kein Tag tragen, hängt der Item-Typname mit am Tag.
 */
const itemTypenames: Partial<Record<keyof typeof cacheTags, string[]>> = {
  SUBJECTS: ['SubjectItem'],
  TEACHINGBLOCK: ['TeachingBlockItem'],
  TIMETABLE: ['TimetableItem'],
  SUBJECT_CONTAINER: ['SubjectContainerItem'],
  ROOMS: ['RoomItem'],
  GRADE_GROUPS: ['GradeGroupItem'],
  SCHOOL_YEAR: ['SchoolYearItem'],
  TIME_GRID: ['TimeGridItem'],
  PERSONS: ['PersonItem'],
  CLASSES: ['ClassItem'],
  // Klassen liefern ihre Teilungen mit (ClassDivisionItem), die Grunddaten Gruppen als GroupItem.
  DIVISIONS: ['DivisionItem', 'GroupItem', 'ClassDivisionItem'],
  CLASS_DIVISIONS: ['DivisionItem', 'GroupItem', 'ClassDivisionItem'],
  GROUPS: ['GroupItem'],
  CURRICULA: ['CurriculumItem'],
  CURRICULUM_VERSIONS: ['CurriculumVersionItem'],
};

function getCacheTag(tag: keyof typeof cacheTags, suffix?: string | null) {
  return {
    additionalTypenames: [cacheTags[tag] + (suffix ? `-${suffix}` : ''), ...(itemTypenames[tag] ?? [])],
  };
}

function getCacheTags(tags: Array<keyof typeof cacheTags>) {
  return {
    additionalTypenames: tags.flatMap((tag) => [cacheTags[tag], ...(itemTypenames[tag] ?? [])]),
  };
}

export { useMemorizedCacheTag, getCacheTag, useMemorizedCacheTags, getCacheTags };
